/**
 * @typedef {Object} Store
 * @property {string} id - Store unique identifier
 * @property {string} [image] - Store image URL
 * @property {string} [title] - Store name
 * @property {string} [address] - Store address
 * @property {string} [city] - Store city
 * @property {string} [state] - Store state abbreviation
 * @property {string} [opening_hours] - Store opening hours
 * @property {string} [wpp_contact] - WhatsApp contact number
 * @property {string} [latitude] - Store latitude
 * @property {string} [longitude] - Store longitude
 */

/**
 * @typedef {Object} City
 * @property {string} name - City name
 * @property {string|null} state - State abbreviation
 * @property {Store[]} stores - Array of stores in the city
 */

/**
 * StoresLocations - Custom element for managing store locator functionality
 */
class StoresLocations extends HTMLElement {
  /**
   * @type {Store[]}
   */
  stores = [];

  /**
   * @type {string|null}
   */
  selectedCity = null;

  /**
   * @type {string|null}
   */
  citySelectedByLocation = null;

  /**
   * @type {Store|null}
   */
  selectedStore = null;

  /**
   * @type {Set<string>}
   */
  prefetchedImages = new Set();

  /**
   * @type {number|null}
   */
  userLatitude = null;

  /**
   * @type {number|null}
   */
  userLongitude = null;

  /**
   * @type {((e: Event) => void)|null}
   */
  storeListClickHandler = null;

  constructor() {
    super();
    this.resizeHandler = this.handleResize.bind(this);
  }

  connectedCallback() {
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (scriptTag && scriptTag.textContent) {
      try {
        const data = JSON.parse(scriptTag.textContent.trim());
        this.stores = Array.isArray(data.stores) ? data.stores : [];
      } catch (e) {
        this.stores = [];
      }
    } else {
      this.stores = [];
    }

    const cities = this.extractCities();
    this.populateCitySelect(cities);
    this.initializeEventListeners();
    window.addEventListener('resize', this.resizeHandler);
    this.hideStoresContainer();
    this.prefetchFirstImagesForAllCities(cities);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  handleResize() {
    if (window.innerWidth > 768) {
      this.cleanupMobileAccordions();
    }
  }

  cleanupMobileAccordions() {
    const container = this.querySelector(
      '.stores-locations-page-new__stores-container'
    );
    if (!container) return;

    const storesRight = this.querySelector(
      '.stores-locations-page-new__stores-right'
    );
    const storesCenter = this.querySelector(
      '.stores-locations-page-new__stores-center'
    );
    const storeList = container.querySelector(
      '.stores-locations-page-new__stores-left'
    );

    const allContentContainers = this.querySelectorAll(
      '.stores-locations-page-new__store-item-content'
    );
    let needsReorder = false;

    allContentContainers.forEach(contentContainer => {
      if (storesRight && contentContainer.contains(storesRight)) {
        needsReorder = true;
      }
      if (storesCenter && contentContainer.contains(storesCenter)) {
        needsReorder = true;
      }
      contentContainer.remove();
    });

    if (needsReorder && storeList) {
      if (storesCenter) {
        storeList.parentElement?.insertBefore(
          storesCenter,
          storeList.nextSibling
        );
      }
      if (storesRight && storesCenter) {
        storesCenter.parentElement?.insertBefore(
          storesRight,
          storesCenter.nextSibling
        );
      } else if (storesRight && storeList) {
        storeList.parentElement?.insertBefore(
          storesRight,
          storeList.nextSibling
        );
      }
    }
    const allItems = this.querySelectorAll(
      '.stores-locations-page-new__store-item'
    );
    allItems.forEach(item => {
      item.classList.remove('stores-locations-page-new__store-item--expanded');
      item.classList.remove('stores-locations-page-new__store-item--active');
      item.setAttribute('aria-pressed', 'false');
    });
  }

  extractCities() {
    const cityMap = new Map();

    this.stores.forEach(store => {
      if (store.city) {
        const cityKey = store.city;
        if (!cityMap.has(cityKey)) {
          cityMap.set(cityKey, {
            name: store.city,
            state: store.state || null,
            stores: [],
          });
        }
        const cityData = cityMap.get(cityKey);
        if (cityData && Array.isArray(cityData.stores)) {
          cityData.stores.push(store);
        }
      }
    });

    return Array.from(cityMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  /**
   * @param {City[]} cities
   */
  populateCitySelect(cities) {
    const select = /** @type {HTMLSelectElement|null} */ (
      this.querySelector('.stores-locations-page-new__city-select')
    );
    if (!select) return;

    const placeholder = select.options[0];
    select.innerHTML = '';
    if (placeholder) {
      select.appendChild(placeholder);
    }

    cities.forEach((/** @type {City} */ city) => {
      const option = document.createElement('option');
      option.value = city.name;
      option.textContent = `${city.name} (${city.stores.length})`;
      select.appendChild(option);
    });
  }

  initializeEventListeners() {
    const select = /** @type {HTMLSelectElement|null} */ (
      this.querySelector('.stores-locations-page-new__city-select')
    );
    if (select) {
      select.addEventListener('change', e => {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        this.handleCityChange(target.value);
      });
    }

    const locationButton = /** @type {HTMLButtonElement|null} */ (
      this.querySelector('.stores-locations-page-new__location-button')
    );
    if (locationButton) {
      locationButton.addEventListener('click', () => {
        this.requestUserLocation();
      });
    }
  }

  requestUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const select = /** @type {HTMLSelectElement|null} */ (
          this.querySelector('.stores-locations-page-new__city-select')
        );
        if (select) {
          select.value = '';
        }

        this.userLatitude = position.coords.latitude;
        this.userLongitude = position.coords.longitude;

        const nearbyCity = this.findNearbyStoreCity(50);
        if (nearbyCity) {
          this.updateLocationButtonText('confira abaixo as lojas próximas');
          this.citySelectedByLocation = nearbyCity;
          this.handleCityChange(nearbyCity, true);
        } else {
          this.updateLocationButtonText('ainda não existem lojas próximas');
          this.citySelectedByLocation = null;
          this.displayNoStoresFound();
        }
      },
      () => {
        this.updateLocationButtonText('habilite a localização');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  /**
   * @param {number} lat1
   * @param {number} lon1
   * @param {number} lat2
   * @param {number} lon2
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  /**
   * @param {number} degrees
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  findNearbyStoreCity(radiusKm = 50) {
    if (
      this.userLatitude === null ||
      this.userLongitude === null ||
      !this.stores ||
      this.stores.length === 0
    ) {
      return null;
    }

    const userLat = this.userLatitude;
    const userLon = this.userLongitude;

    let nearestStore = null;
    let nearestDistance = Infinity;

    for (const store of this.stores) {
      if (!store.latitude || !store.longitude || !store.city) {
        continue;
      }

      const storeLat = parseFloat(store.latitude);
      const storeLon = parseFloat(store.longitude);

      if (isNaN(storeLat) || isNaN(storeLon)) {
        continue;
      }

      const distance = this.calculateDistance(
        userLat,
        userLon,
        storeLat,
        storeLon
      );

      if (distance <= radiusKm && distance < nearestDistance) {
        nearestDistance = distance;
        nearestStore = store;
      }
    }

    return nearestStore && nearestStore.city ? nearestStore.city : null;
  }

  /**
   * @param {string} cityName
   * @param {boolean} [isFromLocation]
   */
  handleCityChange(cityName, isFromLocation = false) {
    if (!cityName) {
      this.hideStoresContainer();
      return;
    }

    this.selectedCity = cityName;

    if (!isFromLocation) {
      this.citySelectedByLocation = null;
      this.resetLocationButtonText();
    }

    const cityData = this.extractCities().find(c => c.name === cityName);

    if (cityData) {
      this.displayStores(cityData);

      if (isFromLocation || this.citySelectedByLocation === cityName) {
        const sortedStores = this.sortStoresByDistance(cityData.stores);
        const firstStore = sortedStores[0];
        if (firstStore) {
          this.selectStore(firstStore);
        }
      } else {
        const firstStore = cityData.stores[0];
        if (firstStore) {
          this.selectStore(firstStore);
        }
      }

      this.prefetchRemainingImagesForCity(cityData);
    }
  }

  /**
   * @param {Store[]} stores
   * @returns {Store[]}
   */
  sortStoresByDistance(stores) {
    if (
      this.userLatitude === null ||
      this.userLongitude === null ||
      !stores ||
      stores.length === 0
    ) {
      return stores;
    }

    const userLat = this.userLatitude;
    const userLon = this.userLongitude;

    const storesWithDistance = stores.map(store => {
      if (!store.latitude || !store.longitude) {
        return { store, distance: Infinity };
      }

      const lat = parseFloat(store.latitude);
      const lon = parseFloat(store.longitude);

      if (isNaN(lat) || isNaN(lon)) {
        return { store, distance: Infinity };
      }

      const distance = this.calculateDistance(userLat, userLon, lat, lon);
      return { store, distance };
    });

    storesWithDistance.sort((a, b) => a.distance - b.distance);

    return storesWithDistance.map(item => item.store);
  }

  /**
   * @param {City} cityData
   */
  displayStores(cityData) {
    const container = this.querySelector(
      '.stores-locations-page-new__stores-container'
    );
    if (!container) return;

    this.cleanupMobileAccordions();
    const cityInfo = container.querySelector(
      '.stores-locations-page-new__city-info'
    );
    if (cityInfo) {
      const stateEl = cityInfo.querySelector(
        '.stores-locations-page-new__state'
      );
      const cityEl = cityInfo.querySelector('.stores-locations-page-new__city');
      const countEl = cityInfo.querySelector(
        '.stores-locations-page-new__count'
      );

      if (stateEl)
        stateEl.textContent = cityData.state ? `${cityData.state},` : '';
      if (cityEl) cityEl.textContent = cityData.name;
      if (countEl)
        countEl.textContent = `[${cityData.stores.length} ${cityData.stores.length > 1 ? 'LOJAS' : 'LOJA'}]`;
    }

    const shouldSortByDistance =
      this.citySelectedByLocation === cityData.name &&
      this.userLatitude !== null &&
      this.userLongitude !== null;

    const storesToDisplay = shouldSortByDistance
      ? this.sortStoresByDistance(cityData.stores)
      : cityData.stores;

    const storeList = container.querySelector(
      '.stores-locations-page-new__store-list'
    );
    if (storeList) {
      if (this.storeListClickHandler) {
        storeList.removeEventListener('click', this.storeListClickHandler);
        this.storeListClickHandler = null;
      }

      const title = storeList.querySelector(
        '.stores-locations-page-new__store-list-title'
      );
      storeList.innerHTML = '';
      if (title) {
        storeList.appendChild(title);
      }

      storeList.setAttribute('role', 'list');

      const fragment = document.createDocumentFragment();

      storesToDisplay.forEach((/** @type {Store} */ store) => {
        const listItem = document.createElement('button');
        listItem.type = 'button';
        listItem.className = 'stores-locations-page-new__store-item';
        listItem.setAttribute('data-store-id', store.id);
        listItem.setAttribute('role', 'listitem');
        listItem.setAttribute('aria-pressed', 'false');
        listItem.setAttribute('aria-controls', 'store-details-panel');

        const itemText = document.createElement('span');
        itemText.textContent = store.title || '';
        listItem.appendChild(itemText);

        const icon = document.createElement('span');
        icon.className = 'stores-locations-page-new__store-item-icon';
        icon.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        listItem.appendChild(icon);

        fragment.appendChild(listItem);
      });

      storeList.appendChild(fragment);

      this.storeListClickHandler = (/** @type {Event} */ e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        const listItem = /** @type {HTMLElement|null} */ (
          target.closest('.stores-locations-page-new__store-item')
        );
        if (!listItem) return;

        const storeId = listItem.getAttribute('data-store-id');
        if (!storeId) return;

        const store = storesToDisplay.find(s => s.id === storeId);
        if (!store) return;

        if (window.innerWidth <= 768) {
          this.handleMobileStoreClick(listItem, store);
        } else {
          this.selectStore(store);
        }
      };

      storeList.addEventListener('click', this.storeListClickHandler);

      if (storesToDisplay.length > 0 && window.innerWidth <= 768) {
        const firstItem = /** @type {HTMLElement|null} */ (
          storeList.querySelector('.stores-locations-page-new__store-item')
        );
        const firstStore = storesToDisplay[0];
        if (firstItem && firstStore) {
          setTimeout(() => {
            this.handleMobileStoreClick(firstItem, firstStore);
          }, 0);
        }
      }
    }

    const storesContainer = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__stores-container')
    );
    const noStoresContainer = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__no-stores-container')
    );

    if (storesContainer) {
      storesContainer.style.display = 'grid';
      storesContainer.removeAttribute('aria-hidden');
    }
    if (noStoresContainer) {
      noStoresContainer.style.display = 'none';
      noStoresContainer.setAttribute('aria-hidden', 'true');
    }
    this.showStoresContainer();
  }

  /**
   * @param {Store} store
   */
  selectStore(store) {
    this.selectedStore = store;

    const storeItems = this.querySelectorAll(
      '.stores-locations-page-new__store-item'
    );
    storeItems.forEach((/** @type {HTMLElement} */ item) => {
      item.classList.remove('stores-locations-page-new__store-item--active');
      item.setAttribute('aria-pressed', 'false');
      const storeId = item.getAttribute('data-store-id');
      if (storeId === store.id) {
        item.classList.add('stores-locations-page-new__store-item--active');
        item.setAttribute('aria-pressed', 'true');
      }
    });

    this.updateStoreDetails(store);
    this.updateTimelineActiveStore(store);
  }

  /**
   * @param {HTMLElement} listItem
   * @param {Store} store
   */
  handleMobileStoreClick(listItem, store) {
    const storesContainer = this.querySelector(
      '.stores-locations-page-new__stores-container'
    );
    const storesRight = this.querySelector(
      '.stores-locations-page-new__stores-right'
    );
    const storesCenter = this.querySelector(
      '.stores-locations-page-new__stores-center'
    );
    if (!storesRight || !storesContainer || !listItem.parentElement) return;

    this.selectedStore = store;
    this.updateStoreDetails(store);
    this.updateTimelineActiveStore(store);

    const isExpanded = listItem.classList.contains(
      'stores-locations-page-new__store-item--expanded'
    );

    this.cleanupMobileAccordions();

    if (!isExpanded) {
      listItem.classList.add('stores-locations-page-new__store-item--expanded');
      listItem.classList.add('stores-locations-page-new__store-item--active');
      listItem.setAttribute('aria-pressed', 'true');

      const contentContainer = document.createElement('div');
      contentContainer.className =
        'stores-locations-page-new__store-item-content';

      if (storesCenter) {
        contentContainer.appendChild(storesCenter);
      }
      contentContainer.appendChild(storesRight);

      listItem.parentElement?.insertBefore(
        contentContainer,
        listItem.nextSibling
      );
    }
  }

  /**
   * Helper function to safely render text with <br> tags using DOM nodes
   * @param {HTMLElement} container
   * @param {string} text
   */
  renderTextWithBreaks(container, text) {
    container.textContent = '';

    if (!text) return;

    const parts = text.split(/<br\s*\/?>/gi);

    parts.forEach((part, index) => {
      if (part) {
        container.appendChild(document.createTextNode(part));
      }
      if (index < parts.length - 1) {
        container.appendChild(document.createElement('br'));
      }
    });
  }

  /**
   * @param {Store} store
   */
  updateStoreDetails(store) {
    const imageContainer = this.querySelector(
      '.stores-locations-page-new__store-image'
    );
    if (imageContainer && store.image) {
      const img = imageContainer.querySelector('img');
      if (img) {
        let imageUrl = String(store.image).trim();
        imageUrl = imageUrl.replace(/^['"]|['"]$/g, '');
        imageUrl = imageUrl.replace(/&amp;/g, '&');
        imageUrl = imageUrl.replace(/([^:]\/)\/+/g, '$1');

        img.src = imageUrl;
        img.alt = (store.title || 'Store image').replace(/^['"]|['"]$/g, '');

        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
        if (!img.hasAttribute('width')) {
          img.setAttribute('width', '800');
        }
        if (!img.hasAttribute('height')) {
          img.setAttribute('height', '600');
        }
      }
    }

    const infoContainer = this.querySelector(
      '.stores-locations-page-new__store-info'
    );
    if (!infoContainer) return;

    const titleEl = infoContainer.querySelector(
      '.stores-locations-page-new__store-title'
    );
    if (titleEl) titleEl.textContent = store.title || '';

    const addressEl = /** @type {HTMLElement|null} */ (
      infoContainer.querySelector('.stores-locations-page-new__store-address')
    );
    if (addressEl) {
      if (store.address) {
        this.renderTextWithBreaks(addressEl, store.address);
      } else {
        addressEl.textContent = '';
      }
    }

    const hoursEl = /** @type {HTMLElement|null} */ (
      infoContainer.querySelector('.stores-locations-page-new__store-hours')
    );
    if (hoursEl) {
      if (store.opening_hours) {
        this.renderTextWithBreaks(hoursEl, store.opening_hours);
      } else {
        hoursEl.textContent = '';
      }
    }

    const wppEl = /** @type {HTMLAnchorElement|null} */ (
      infoContainer.querySelector(
        '.stores-locations-page-new__store-wpp-button'
      )
    );
    if (wppEl) {
      const DEFAULT_PHONE_BR_WITHOUT_DDI = '2123919797';
      const isUsingFallbackPhone =
        !store.wpp_contact || store.wpp_contact.trim() === '';
      const raw = store.wpp_contact || DEFAULT_PHONE_BR_WITHOUT_DDI;
      let digits = String(raw).replace(/\D/g, '');

      if (digits.startsWith('55') && digits.length > 11) {
        digits = digits.slice(2);
      }

      wppEl.href = `https://wa.me/55${digits}`;

      const wppTextEl = wppEl.querySelector('span:last-child');
      if (wppTextEl) {
        wppTextEl.textContent = isUsingFallbackPhone
          ? 'fale com o atendimento'
          : 'fale com a loja';
      }
    }
  }

  showStoresContainer() {
    const wrapper = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__stores-wrapper')
    );
    if (wrapper) {
      wrapper.style.display = '';
      wrapper.removeAttribute('aria-hidden');
      setTimeout(() => {
        const wrapperRect = wrapper.getBoundingClientRect();
        const wrapperTop = wrapperRect.top + window.scrollY;
        const scrollPosition = wrapperTop - 150;
        window.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });
      }, 100);
    }
  }

  hideStoresContainer() {
    const wrapper = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__stores-wrapper')
    );
    if (wrapper) {
      wrapper.style.display = 'none';
      wrapper.setAttribute('aria-hidden', 'true');
    }
  }

  displayNoStoresFound() {
    this.cleanupMobileAccordions();

    const storesContainer = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__stores-container')
    );
    const noStoresContainer = /** @type {HTMLElement|null} */ (
      this.querySelector('.stores-locations-page-new__no-stores-container')
    );

    if (storesContainer) {
      storesContainer.style.display = 'none';
      storesContainer.setAttribute('aria-hidden', 'true');
    }
    if (noStoresContainer) {
      noStoresContainer.style.display = 'grid';
      noStoresContainer.removeAttribute('aria-hidden');

      const rightSection = /** @type {HTMLElement|null} */ (
        noStoresContainer.querySelector(
          '.stores-locations-page-new__no-stores-right'
        )
      );
      const leftWrapper = /** @type {HTMLElement|null} */ (
        noStoresContainer.querySelector(
          '.stores-locations-page-new__no-stores-left-wrapper'
        )
      );

      if (rightSection && leftWrapper) {
        const setHeight = () => {
          const leftHeight = leftWrapper.offsetHeight;
          if (leftHeight > 0) {
            rightSection.style.height = `${leftHeight}px`;
          }
        };

        const image = rightSection.querySelector('img');
        if (image) {
          if (image.complete) {
            setHeight();
          } else {
            image.onload = setHeight;
          }
        }
      }

      this.showStoresContainer();
    }
  }

  /**
   * @param {string} text
   */
  updateLocationButtonText(text) {
    const buttonText = this.querySelector(
      '.stores-locations-page-new__location-button-text'
    );
    if (buttonText) {
      buttonText.textContent = text;
    }
  }

  resetLocationButtonText() {
    const buttonText = this.querySelector(
      '.stores-locations-page-new__location-button-text'
    );
    if (buttonText) {
      const originalText = buttonText.getAttribute('data-original-text');
      if (originalText) {
        buttonText.textContent = originalText;
      }
    }
  }

  /**
   * @param {Store} store
   */
  updateTimelineActiveStore(store) {
    if (!store || !store.title) return;

    const timelineTable = document.querySelector('.store-timeline-table');
    if (!timelineTable) return;

    const tbody = timelineTable.querySelector('tbody');
    if (!tbody) return;

    const rows = tbody.querySelectorAll('tr[data-store-title]');
    rows.forEach(row => {
      row.classList.remove('active');
    });

    const matchingRow = Array.from(rows).find(row => {
      const rowTitle = row.getAttribute('data-store-title') || '';
      return rowTitle === store.title;
    });

    if (matchingRow) {
      matchingRow.classList.add('active');
    }
  }

  /**
   * @param {string} imageUrl
   */
  prefetchImage(imageUrl) {
    if (!imageUrl || this.prefetchedImages.has(imageUrl)) {
      return;
    }

    let cleanUrl = String(imageUrl).trim();
    cleanUrl = cleanUrl.replace(/^['"]|['"]$/g, '');
    cleanUrl = cleanUrl.replace(/&amp;/g, '&');
    cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, '$1');

    if (!cleanUrl) return;

    this.prefetchedImages.add(cleanUrl);
    const img = new Image();
    img.src = cleanUrl;
  }

  /**
   * @param {City[]} cities
   */
  prefetchFirstImagesForAllCities(cities) {
    cities.forEach((/** @type {City} */ city) => {
      if (city.stores && city.stores.length > 0) {
        const firstStore = city.stores[0];
        if (firstStore && firstStore.image) {
          this.prefetchImage(firstStore.image);
        }
      }
    });
  }

  /**
   * @param {City} cityData
   */
  prefetchRemainingImagesForCity(cityData) {
    if (!cityData || !cityData.stores || cityData.stores.length <= 1) {
      return;
    }

    const remainingStores = cityData.stores.slice(1);
    remainingStores.forEach((/** @type {Store} */ store) => {
      if (store && store.image) {
        this.prefetchImage(store.image);
      }
    });
  }
}

customElements.define('stores-locations-page-new', StoresLocations);
